import {useEffect, useState} from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {Input, RadioButtonGroup} from 'components/forms/inputs'
import {alertTriggerTypes} from 'components/filters/constants'
import type {Trigger} from 'services/types'
import type {AlertStageProps} from './types'
import {
  MODEL_AVAILABILITY,
  MODEL_CHANGE,
  MODEL_RANK_CHANGE,
  NEW_EVALUATION,
  NEW_MODEL,
  SCORE_THRESHOLD,
  TOP_MODEL,
} from 'services/constants'
import FilterItems from './FilterItems'

type FilterProps = Trigger & {onChange: (field: keyof Trigger, v: any) => void}
const FilterModels = (props: FilterProps) => (
  <Stack spacing={4} justifyContent="space-around">
    <FilterItems itemType="model" {...props} />
  </Stack>
)

const FilterModelsEvals = (props: FilterProps) => (
  <Stack spacing={4} justifyContent="space-around">
    <FilterItems itemType="model" {...props} />
    <FilterItems itemType="evaluation" {...props} />
  </Stack>
)

const FilterScoreThreshold = (props: FilterProps) => {
  const [threshold, setThreshold] = useState<string | undefined>(props.threshold?.toString() || '')

  const onThreshold = (v: string) => {
    if (!v) {
      setThreshold(v)
      props.onChange('threshold', undefined)
    }

    const num = parseFloat(v)
    if (num > 100 || num < 0) return

    if (!isNaN(num)) {
      setThreshold(v)
      props.onChange('threshold', num)
    }
  }

  return (
    <Stack spacing={4} justifyContent="space-around">
      <Typography variant="h4">At what score should the alert trigger?</Typography>
      <Input
        setter={onThreshold}
        value={threshold}
        type="number"
        sx={{width: 200}}
        min={0}
        max={100}
        required={true}
      />
      <FilterItems itemType="model" {...props} />
    </Stack>
  )
}

const TriggerFilters = (props: FilterProps) => {
  switch (props.type) {
    case TOP_MODEL:
    case MODEL_RANK_CHANGE:
      return <FilterModelsEvals {...props} />
    case MODEL_AVAILABILITY:
    case MODEL_CHANGE:
    case NEW_MODEL:
      return <FilterModels {...props} />
    case SCORE_THRESHOLD:
      return <FilterScoreThreshold {...props} />
    case NEW_EVALUATION:
    default:
      return null
  }
}

const emptyChosen = Object.keys(alertTriggerTypes).reduce(
  (acc, t) => ({...acc, [t]: {type: t}}),
  {}
) as {[k: string]: Trigger}

type ChooseTriggerProps = AlertStageProps & {trigger: Trigger; triggerChanged: (t: Trigger) => void}
const ChooseTrigger = ({trigger, triggerChanged, enableNext, index}: ChooseTriggerProps) => {
  const [chosen, setChosen] = useState(emptyChosen)
  const [selected, setSelected] = useState<string | undefined>(trigger?.type)

  useEffect(() => {
    enableNext(!!selected)
  }, [index, enableNext, selected])

  const handleChange = (selected?: string) => {
    setSelected(selected)
    triggerChanged(selected ? chosen[selected] : ({} as Trigger))
  }

  const handleFiltersChange = (field: keyof Trigger, v: any) => {
    const updated = {...trigger, [field]: v}
    setChosen((current) => ({...current, [trigger.type]: updated}))
    triggerChanged(updated)
  }

  return (
    <Stack spacing={4} sx={{pb: 4}}>
      <RadioButtonGroup
        perRow={4}
        selected={selected}
        onSelect={handleChange}
        items={Object.entries(alertTriggerTypes).map(([id, label]) => ({id, label}))}
      />
      {selected && <TriggerFilters {...trigger} onChange={handleFiltersChange} />}
    </Stack>
  )
}

export default ChooseTrigger
